/**
 * MarqueeAdminPage - Admin page for managing marquee announcements
 * [PHASE 4.4] Marquee Feature
 *
 * Features:
 * - Global settings section (admin+ only)
 * - Marquee list management (moderator+ only)
 * - Create/Edit marquee via modal
 * - Responsive design
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import DesktopLayout from '../../components/layout/DesktopLayout';
import Modal from '../../components/common/Modal';
import MarqueeManager from '../../components/admin/MarqueeManager';
import MarqueeForm from '../../components/admin/MarqueeForm';
import {
  getSettings,
  updateSettings,
  getAllMarquees,
  createMarquee,
  updateMarquee,
  deleteMarquee,
  toggleMarquee,
  reorderMarquee,
} from '../../services/marqueeService';
import type { MarqueeAdmin, CreateMarqueeDTO, UpdateMarqueeDTO, MarqueeSettings, MarqueeOrderItem } from '../../types';
import './MarqueeAdminPage.css';

const MarqueeAdminPage = () => {
  const { hasRole } = useAuth();

  // Permissions
  const canManageSettings = hasRole(['admin', 'super_admin']);
  const canManageMarquees = hasRole(['moderator', 'admin', 'super_admin']);

  // State for global settings
  const [settings, setSettings] = useState<MarqueeSettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSettingsCollapsed, setIsSettingsCollapsed] = useState(true); // 預設折疊

  // State for marquee list
  const [marquees, setMarquees] = useState<MarqueeAdmin[]>([]);
  const [isLoadingMarquees, setIsLoadingMarquees] = useState(true);
  const [marqueeMessage, setMarqueeMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // State for modal
  const [showModal, setShowModal] = useState(false);
  const [editingMarquee, setEditingMarquee] = useState<MarqueeAdmin | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch settings on mount
  useEffect(() => {
    if (canManageSettings) {
      fetchSettings();
    }
  }, [canManageSettings]);

  // Fetch marquees on mount
  useEffect(() => {
    if (canManageMarquees) {
      fetchMarquees();
    }
  }, [canManageMarquees]);

  const fetchSettings = async () => {
    setIsLoadingSettings(true);
    setSettingsMessage(null);

    try {
      const response = await getSettings();

      if (response.success && response.data) {
        setSettings(response.data);
      } else {
        setSettingsMessage({ type: 'error', text: response.error?.message || '載入設定失敗' });
      }
    } catch (error) {
      setSettingsMessage({ type: 'error', text: '網路錯誤，無法載入設定' });
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const fetchMarquees = async () => {
    setIsLoadingMarquees(true);
    setMarqueeMessage(null);

    try {
      const response = await getAllMarquees();

      if (response.success && response.data) {
        // Backend returns { success: true, data: [...], pagination: {...} }
        // api.get extracts data.data which is the array
        setMarquees(response.data as MarqueeAdmin[]);
      } else {
        setMarqueeMessage({ type: 'error', text: response.error?.message || '載入跑馬燈失敗' });
      }
    } catch (error) {
      console.error('fetchMarquees error:', error);
      setMarqueeMessage({ type: 'error', text: '網路錯誤，無法載入跑馬燈' });
    } finally {
      setIsLoadingMarquees(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    setIsSavingSettings(true);
    setSettingsMessage(null);

    try {
      const response = await updateSettings(settings);

      if (response.success && response.data) {
        setSettings(response.data);
        setSettingsMessage({ type: 'success', text: '設定已儲存' });
      } else {
        setSettingsMessage({ type: 'error', text: response.error?.message || '儲存設定失敗' });
      }
    } catch (error) {
      setSettingsMessage({ type: 'error', text: '網路錯誤，無法儲存設定' });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleCreateMarquee = async (data: CreateMarqueeDTO) => {
    setIsSubmitting(true);
    setMarqueeMessage(null);

    try {
      const response = await createMarquee(data);

      if (response.success && response.data) {
        setMarqueeMessage({ type: 'success', text: '跑馬燈已新增' });
        setShowModal(false);
        fetchMarquees();
      } else {
        setMarqueeMessage({ type: 'error', text: response.error?.message || '新增跑馬燈失敗' });
      }
    } catch (error) {
      setMarqueeMessage({ type: 'error', text: '網路錯誤，無法新增跑馬燈' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateMarquee = async (data: UpdateMarqueeDTO) => {
    if (!editingMarquee) return;

    setIsSubmitting(true);
    setMarqueeMessage(null);

    try {
      const response = await updateMarquee(editingMarquee.id, data);

      if (response.success && response.data) {
        setMarqueeMessage({ type: 'success', text: '跑馬燈已更新' });
        setShowModal(false);
        setEditingMarquee(null);
        fetchMarquees();
      } else {
        setMarqueeMessage({ type: 'error', text: response.error?.message || '更新跑馬燈失敗' });
      }
    } catch (error) {
      setMarqueeMessage({ type: 'error', text: '網路錯誤，無法更新跑馬燈' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMarquee = async (id: string) => {
    setMarqueeMessage(null);

    try {
      const response = await deleteMarquee(id);

      if (response.success) {
        setMarqueeMessage({ type: 'success', text: '跑馬燈已刪除' });
        fetchMarquees();
      } else {
        setMarqueeMessage({ type: 'error', text: response.error?.message || '刪除跑馬燈失敗' });
      }
    } catch (error) {
      setMarqueeMessage({ type: 'error', text: '網路錯誤，無法刪除跑馬燈' });
    }
  };

  const handleToggleMarquee = async (id: string) => {
    setMarqueeMessage(null);

    try {
      const response = await toggleMarquee(id);

      if (response.success) {
        fetchMarquees();
      } else {
        setMarqueeMessage({ type: 'error', text: response.error?.message || '切換狀態失敗' });
      }
    } catch (error) {
      setMarqueeMessage({ type: 'error', text: '網路錯誤，無法切換狀態' });
    }
  };

  const handleReorderMarquee = async (orders: MarqueeOrderItem[]) => {
    // Optimistic update: 立即更新前端顯示，不等 API
    const updatedMarquees = [...marquees].map((marquee) => {
      const orderUpdate = orders.find(o => o.id === marquee.id);
      return orderUpdate
        ? { ...marquee, displayOrder: orderUpdate.displayOrder }
        : marquee;
    });

    // 按新的 displayOrder 排序
    const sortedMarquees = updatedMarquees.sort((a, b) => a.displayOrder - b.displayOrder);
    setMarquees(sortedMarquees);

    // 背景更新 API，失敗時才回滾
    try {
      const response = await reorderMarquee(orders);

      if (!response.success) {
        throw new Error(response.error?.message || '排序失敗');
      }
      // 成功時不需要重新載入，前端已經是最新狀態
    } catch (error) {
      // API 失敗，回滾到原來的順序
      console.error('❌ [MarqueeAdmin] Reorder failed, rolling back:', error);
      setMarqueeMessage({
        type: 'error',
        text: '排序更新失敗，已回復原順序',
      });
      fetchMarquees(); // 重新載入正確的順序
      throw error;
    }
  };

  const handleEditClick = (marquee: MarqueeAdmin) => {
    setEditingMarquee(marquee);
    setShowModal(true);
  };

  const handleAddClick = () => {
    // Check if marquee limit is reached (15 max)
    const MAX_MARQUEES = 15;
    if (marquees.length >= MAX_MARQUEES) {
      setMarqueeMessage({
        type: 'error',
        text: `跑馬燈數量已達上限 (${MAX_MARQUEES} 則)，無法新增更多跑馬燈`,
      });
      return;
    }
    setEditingMarquee(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMarquee(null);
  };

  // Wrapper function to handle both create and update operations
  // Type: (data: CreateMarqueeDTO | UpdateMarqueeDTO) => Promise<void>
  const handleSubmit = async (data: CreateMarqueeDTO | UpdateMarqueeDTO): Promise<void> => {
    if (editingMarquee) {
      await handleUpdateMarquee(data as UpdateMarqueeDTO);
    } else {
      await handleCreateMarquee(data as CreateMarqueeDTO);
    }
  };

  const getStats = () => {
    const activeCount = marquees.filter(m => m.isActive).length;
    const totalCount = marquees.length;
    return { activeCount, totalCount };
  };

  // Render access denied message
  if (!canManageMarquees) {
    return (
      <div className="marquee-admin-page">
        <DesktopLayout>
        <main className="marquee-admin-page__main">
          <div className="marquee-admin-page__container">
            <div className="marquee-admin-page__denied">
              <h1>存取被拒絕</h1>
              <p>您需要至少版主權限才能訪問此頁面</p>
            </div>
          </div>
        </main>
        </DesktopLayout>
      </div>
    );
  }

  return (
    <div className="marquee-admin-page">
      <DesktopLayout>
      <main className="marquee-admin-page__main">
        <div className="marquee-admin-page__container">
          {/* Page Header */}
          <header className="marquee-admin-page__header">
            <div>
              <h1 className="marquee-admin-page__title">跑馬燈管理</h1>
              <p className="marquee-admin-page__subtitle">管理網站跑馬燈公告與全域設定</p>
            </div>
            <button
              onClick={handleAddClick}
              className="marquee-admin-page__add-btn"
            >
              + 新增跑馬燈
            </button>
          </header>

          {/* Global Settings Section (Admin+ only) */}
          {canManageSettings && (
            <section className="marquee-admin-page__settings">
              <div
                className="marquee-admin-page__section-header marquee-admin-page__section-header--collapsible"
                onClick={() => setIsSettingsCollapsed(!isSettingsCollapsed)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className={`marquee-admin-page__collapse-icon ${isSettingsCollapsed ? '' : 'marquee-admin-page__collapse-icon--expanded'}`}>
                    ▶
                  </span>
                  <h2 className="marquee-admin-page__section-title">全域設定</h2>
                </div>
                {!isSettingsCollapsed && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveSettings();
                    }}
                    className="marquee-admin-page__save-btn"
                    disabled={isSavingSettings || isLoadingSettings}
                  >
                    {isSavingSettings ? '儲存中...' : '儲存設定'}
                  </button>
                )}
              </div>

              {!isSettingsCollapsed && (
                <>
                  {settingsMessage && (
                    <div className={`marquee-admin-page__message marquee-admin-page__message--${settingsMessage.type}`}>
                      {settingsMessage.text}
                      <button
                        onClick={() => setSettingsMessage(null)}
                        className="marquee-admin-page__message-close"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {isLoadingSettings ? (
                    <div className="marquee-admin-page__loading">載入中...</div>
                  ) : settings ? (
                    <div className="marquee-admin-page__settings-form">
                  {/* Enable Toggle */}
                  <div className="marquee-admin-page__form-field">
                    <label className="marquee-admin-page__checkbox-label">
                      <input
                        type="checkbox"
                        checked={settings.enabled}
                        onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                        className="marquee-admin-page__checkbox"
                      />
                      <span>啟用跑馬燈功能</span>
                    </label>
                    <p className="marquee-admin-page__field-hint">
                      關閉後所有跑馬燈將不會顯示
                    </p>
                  </div>

                  {/* Default Speed */}
                  <div className="marquee-admin-page__form-field">
                    <label className="marquee-admin-page__label">
                      預設滾動速度: {settings.defaultSpeed} 秒
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="30"
                      step="1"
                      value={settings.defaultSpeed}
                      onChange={(e) => setSettings({ ...settings, defaultSpeed: Number(e.target.value) })}
                      className="marquee-admin-page__slider"
                    />
                    <div className="marquee-admin-page__slider-labels">
                      <span>10秒 (快)</span>
                      <span>30秒 (慢)</span>
                    </div>
                  </div>

                  {/* Default Colors */}
                  <div className="marquee-admin-page__form-row">
                    <div className="marquee-admin-page__form-field">
                      <label className="marquee-admin-page__label">預設文字顏色</label>
                      <div className="marquee-admin-page__color-input">
                        <input
                          type="color"
                          value={settings.defaultTextColor}
                          onChange={(e) => setSettings({ ...settings, defaultTextColor: e.target.value })}
                          className="marquee-admin-page__color-picker"
                        />
                        <input
                          type="text"
                          value={settings.defaultTextColor}
                          onChange={(e) => setSettings({ ...settings, defaultTextColor: e.target.value })}
                          className="marquee-admin-page__color-text"
                          maxLength={7}
                        />
                      </div>
                    </div>

                    <div className="marquee-admin-page__form-field">
                      <label className="marquee-admin-page__label">預設背景顏色</label>
                      <div className="marquee-admin-page__color-input">
                        <input
                          type="color"
                          value={settings.defaultBgColor}
                          onChange={(e) => setSettings({ ...settings, defaultBgColor: e.target.value })}
                          className="marquee-admin-page__color-picker"
                        />
                        <input
                          type="text"
                          value={settings.defaultBgColor}
                          onChange={(e) => setSettings({ ...settings, defaultBgColor: e.target.value })}
                          className="marquee-admin-page__color-text"
                          maxLength={7}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Default Opacity */}
                  <div className="marquee-admin-page__form-field">
                    <label className="marquee-admin-page__label">
                      預設背景透明度: {settings.defaultBgOpacity.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.defaultBgOpacity}
                      onChange={(e) => setSettings({ ...settings, defaultBgOpacity: Number(e.target.value) })}
                      className="marquee-admin-page__slider"
                    />
                    <div className="marquee-admin-page__slider-labels">
                      <span>0.0 (透明)</span>
                      <span>1.0 (不透明)</span>
                    </div>
                  </div>
                </div>
              ) : null}
                </>
              )}
            </section>
          )}

          {/* Marquee List Section */}
          <section className="marquee-admin-page__list">
            <div className="marquee-admin-page__section-header">
              <h2 className="marquee-admin-page__section-title">跑馬燈列表</h2>
              <div className="marquee-admin-page__stats">
                已啟用 {getStats().activeCount} / 總計 {getStats().totalCount} / 上限 15
              </div>
            </div>

            {marqueeMessage && (
              <div className={`marquee-admin-page__message marquee-admin-page__message--${marqueeMessage.type}`}>
                {marqueeMessage.text}
                <button
                  onClick={() => setMarqueeMessage(null)}
                  className="marquee-admin-page__message-close"
                >
                  ×
                </button>
              </div>
            )}

            {isLoadingMarquees ? (
              <div className="marquee-admin-page__loading">載入中...</div>
            ) : (
              <MarqueeManager
                marquees={marquees}
                onEdit={handleEditClick}
                onDelete={handleDeleteMarquee}
                onToggle={handleToggleMarquee}
                onReorder={handleReorderMarquee}
                defaultSpeed={settings?.defaultSpeed}
              />
            )}
          </section>
        </div>
      </main>

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingMarquee ? '編輯跑馬燈' : '新增跑馬燈'}
          size="large"
          desktopOnly
        >
          <MarqueeForm
            initialData={editingMarquee || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            isLoading={isSubmitting}
            defaultSpeed={settings?.defaultSpeed}
          />
        </Modal>
      )}
      </DesktopLayout>
    </div>
  );
};

export default MarqueeAdminPage;
